import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Mail, MessageCircle, MousePointer2, TrendingUp, ShieldOff, Zap } from "lucide-react";

export default function AdminAttribution() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [recent, setRecent] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [couponsPerf, setCouponsPerf] = useState([]);

  useEffect(() => {
    const q = `?days=${days}`;
    Promise.all([
      api.get(`/admin/attribution/summary${q}`),
      api.get(`/admin/attribution/campaigns${q}`),
      api.get(`/admin/attribution/recent-clicks?limit=20`),
      api.get(`/admin/attribution/suppressions`),
      api.get(`/admin/attribution/coupons-performance${q}`),
    ]).then(([s, c, r, sup, cp]) => {
      setSummary(s.data);
      setCampaigns(c.data.rows || []);
      setRecent(r.data.clicks || []);
      setSuppressions(sup.data.items || []);
      setCouponsPerf(cp.data.rows || []);
    });
  }, [days]);

  const email = (summary?.channels || []).find((c) => c.channel === "email");
  const wa = (summary?.channels || []).find((c) => c.channel === "whatsapp");

  return (
    <div className="space-y-6" data-testid="admin-attribution">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">تحليلات التسويق (Attribution)</h1>
          <p className="text-foreground/60 mt-2 text-sm">
            كم إيراد جاء من كل قناة (بريد / WhatsApp)، وأيّ رسالة تسويقية أعادت أكبر عدد من العملاء.
          </p>
        </div>
        <div className="flex gap-1">
          {[
            { days: 7,     label: "آخر 7 أيام" },
            { days: 30,    label: "آخر 30 يومًا" },
            { days: 90,    label: "آخر 90 يومًا" },
            // "الكل" uses a very large window (~100 years) so the backend
            // aggregation includes every historical event without needing
            // a special-case branch on the server.
            { days: 36500, label: "الكل" },
          ].map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={`px-3 py-1.5 rounded text-xs border ${
                days === r.days ? "bg-black text-white border-foreground" : "bg-white border-foreground/10"
              }`}
              data-testid={`range-${r.days}`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      {/* Overall */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MetaCard label="رسائل مُرسَلة" value={summary.total.sent} icon={Zap} />
          <MetaCard label="نقرات فريدة" value={summary.total.unique_clicked} icon={MousePointer2} />
          <MetaCard label="عمليات شراء" value={summary.total.purchased} icon={TrendingUp} color="text-green-700" />
          <MetaCard label="إيراد مُسند" value={`${summary.total.revenue.toLocaleString("ar-SA")} SAR`} icon={TrendingUp} color="text-primary" />
        </div>
      )}

      {/* Split by channel */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChannelCard title="البريد الإلكتروني" icon={Mail} accent="blue" data={email} />
        <ChannelCard title="WhatsApp" icon={MessageCircle} accent="green" data={wa} />
      </div>

      {/* Campaigns / steps breakdown */}
      <Card className="p-4 bg-white border-foreground/10">
        <h3 className="font-semibold text-sm mb-3">أداء الحملات والخطوات</h3>
        {campaigns.length === 0 ? (
          <p className="text-xs text-foreground/50">لا توجد بيانات بعد. عند إرسال أول رسائل تسويقية للعملاء، ستظهر الأرقام هنا.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-foreground/10">
                <TableHead>القناة</TableHead>
                <TableHead>الحملة</TableHead>
                <TableHead>الخطوة</TableHead>
                <TableHead className="text-center">مُرسَلة</TableHead>
                <TableHead className="text-center">نقرات فريدة</TableHead>
                <TableHead className="text-center">مشتريات</TableHead>
                <TableHead className="text-center">نسبة النقر</TableHead>
                <TableHead className="text-center">نسبة التحويل</TableHead>
                <TableHead className="text-center">الإيراد</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((r, i) => (
                <TableRow key={i} className="border-foreground/5">
                  <TableCell>{r.channel === "email" ? "بريد" : r.channel === "whatsapp" ? "WhatsApp" : r.channel}</TableCell>
                  <TableCell className="text-xs">{r.campaign}</TableCell>
                  <TableCell className="text-xs">{r.step}</TableCell>
                  <TableCell className="text-center font-mono">{r.sent}</TableCell>
                  <TableCell className="text-center font-mono">{r.unique_clicked}</TableCell>
                  <TableCell className="text-center font-mono">{r.purchased}</TableCell>
                  <TableCell className="text-center font-mono">{r.click_rate_pct}%</TableCell>
                  <TableCell className="text-center font-mono">{r.conversion_rate_pct}%</TableCell>
                  <TableCell className="text-center font-mono">{r.revenue.toLocaleString("ar-SA")} SAR</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Coupon performance — includes coupons used manually (no click) */}
      <Card className="p-4 bg-white border-foreground/10">
        <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-sm">أداء الكوبونات حسب القناة</h3>
            <p className="text-[10px] text-foreground/50 mt-1">
              الكوبون المُوسَم بقناة (مثل SAVE15WA للواتساب) يُنسب إيراده لتلك القناة — حتى لو دخل العميل مباشرة دون النقر على الرابط.
            </p>
          </div>
        </div>
        {couponsPerf.length === 0 ? (
          <p className="text-xs text-foreground/50">لا كوبونات مُوسَمة أو مُستخدَمة بعد. من صفحة "أكواد الخصم" اضغط "إنشاء نُسختين للقنوات" لأي كوبون لإطلاق حملات قابلة للقياس.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-foreground/10">
                <TableHead>الكود</TableHead>
                <TableHead>القناة</TableHead>
                <TableHead>الحملة</TableHead>
                <TableHead className="text-center">مشتريات</TableHead>
                <TableHead className="text-center">الإيراد</TableHead>
                <TableHead className="text-center">الحالة</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {couponsPerf.map((r, i) => (
                <TableRow key={i} className="border-foreground/5">
                  <TableCell className="font-mono text-xs">{r.code}</TableCell>
                  <TableCell>
                    {r.channel === "whatsapp" && <span className="px-1.5 py-0.5 rounded text-[10px] bg-green-50 text-green-700">WhatsApp</span>}
                    {r.channel === "email" && <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700">بريد</span>}
                    {r.channel && !["whatsapp", "email"].includes(r.channel) && <span className="text-xs">{r.channel}</span>}
                    {!r.channel && <span className="text-[10px] text-foreground/40">غير مُوسَم</span>}
                  </TableCell>
                  <TableCell className="text-xs">{r.campaign || "—"}</TableCell>
                  <TableCell className="text-center font-mono">{r.purchases}</TableCell>
                  <TableCell className="text-center font-mono">{r.revenue.toLocaleString("ar-SA")} SAR</TableCell>
                  <TableCell className="text-center">
                    {r.used ? (
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700">مُستخدَم</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-50 text-slate-600">جاهز</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Live feed + suppressions */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4 bg-white border-foreground/10">
          <h3 className="font-semibold text-sm mb-3">آخر النقرات المباشرة</h3>
          {recent.length === 0 ? (
            <p className="text-xs text-foreground/50">لا نقرات حديثة.</p>
          ) : (
            <ul className="space-y-2 text-xs">
              {recent.map((c, i) => (
                <li key={i} className="flex items-center gap-2 border-b border-foreground/5 pb-2 last:border-0">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${c.channel === "whatsapp" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}>
                    {c.channel === "whatsapp" ? "WhatsApp" : "بريد"}
                  </span>
                  <span className="opacity-70">{c.step}</span>
                  <span className="flex-1 truncate">{c.lead_name || c.lead_email}</span>
                  {c.converted && <span className="text-green-700 font-semibold">✓ اشترى</span>}
                  <span className="opacity-40 text-[10px]">{fmtRelative(c.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4 bg-white border-foreground/10">
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <ShieldOff className="h-4 w-4 text-red-600" /> قائمة عدم التواصل ({suppressions.length})
          </h3>
          {suppressions.length === 0 ? (
            <p className="text-xs text-foreground/50">لا أحد في قائمة عدم التواصل — مؤشر جيد على جودة بريدك.</p>
          ) : (
            <ul className="space-y-1 text-xs max-h-72 overflow-y-auto">
              {suppressions.slice(0, 100).map((s, i) => (
                <li key={i} className="flex justify-between border-b border-foreground/5 py-1 last:border-0">
                  <span className="truncate">{s.email || s.phone}</span>
                  <span className="text-foreground/50">{s.channel} · {s.reason}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function MetaCard({ label, value, icon: Icon, color = "text-foreground" }) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-foreground/60">{label}</div>
          <div className={`text-2xl font-display font-semibold mt-1 ${color}`}>{value}</div>
        </div>
        <div className="h-9 w-9 rounded-lg bg-foreground/5 grid place-items-center"><Icon className={`h-4 w-4 ${color}`} /></div>
      </div>
    </Card>
  );
}

function ChannelCard({ title, icon: Icon, accent, data }) {
  const c = data || { sent: 0, delivered: 0, opened: 0, unique_opened: 0, unique_clicked: 0, purchased: 0, revenue: 0, click_rate_pct: 0, conversion_rate_pct: 0, click_to_purchase_pct: 0, open_rate_pct: null };
  const accentClass = accent === "green" ? "text-green-700 bg-green-50 border-green-200" : "text-blue-700 bg-blue-50 border-blue-200";
  return (
    <Card className={`p-4 border ${accentClass}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-5 w-5" />
        <h3 className="font-semibold">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Stat label="مُرسَلة" value={c.sent} />
        <Stat label="تم التسليم" value={c.delivered} />
        {c.open_rate_pct !== null && <Stat label="نسبة الفتح" value={`${c.open_rate_pct}%`} />}
        <Stat label="نقرات فريدة" value={c.unique_clicked} />
        <Stat label="نسبة النقر" value={`${c.click_rate_pct}%`} />
        <Stat label="عمليات شراء" value={c.purchased} bold />
        <Stat label="نسبة التحويل" value={`${c.conversion_rate_pct}%`} />
        <Stat label="من نقرة → شراء" value={`${c.click_to_purchase_pct}%`} />
      </div>
      <div className="mt-3 pt-3 border-t border-current/20 flex justify-between items-baseline">
        <span className="text-xs opacity-80">الإيراد المُسند</span>
        <span className="text-2xl font-bold">{(c.revenue || 0).toLocaleString("ar-SA")} <span className="text-sm">SAR</span></span>
      </div>
    </Card>
  );
}

function Stat({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className="opacity-70">{label}</span>
      <span className={`font-mono ${bold ? "font-bold" : ""}`}>{value}</span>
    </div>
  );
}

function fmtRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.round((now - then) / 1000);
  if (diff < 60) return `منذ ${diff} ث`;
  if (diff < 3600) return `منذ ${Math.round(diff / 60)} د`;
  if (diff < 86400) return `منذ ${Math.round(diff / 3600)} س`;
  return `منذ ${Math.round(diff / 86400)} ي`;
}
